import React, { useState, useEffect } from 'react';
import { supabase, getApiBaseUrl } from '../../services/api';
import { Shield, CheckCircle } from 'lucide-react';
import AuthModal from './AuthModal';
import './ResetPassword.css';

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [linkError, setLinkError] = useState(null);

  useEffect(() => {
    const checkSession = async () => {
      const hash = window.location.hash;
      if (hash.includes('error=')) {
        const params = new URLSearchParams(hash.substring(1));
        const errorDesc = params.get('error_description');
        setLinkError(errorDesc?.replace(/\+/g, ' ') || 'This link is invalid or has expired.');
        setCheckingSession(false);
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 500));
      
      const { data: { session } } = await supabase.auth.getSession();
      setHasSession(!!session);
      setCheckingSession(false);
    };

    checkSession();
  }, []);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    const { error } = await supabase.auth.updateUser({ password: newPassword });

    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      // Supabase may not add an "email" identity/provider for OAuth users who
      // set a password via reset. Persist Email as a login method before sign-out.
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          await fetch(`${getApiBaseUrl()}/user/enable-email-login`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
        }
      } catch (err) {
        console.warn('Failed to enable email login provider after reset:', err);
      }

      await supabase.auth.signOut();
      setSuccess(true);
      setLoading(false);
    }
  };

  const handleShowLogin = () => setShowAuthModal(true);
  
  const handleLoginSuccess = async () => {
    if (window.handleSuccessfulLogin) {
      await window.handleSuccessfulLogin();
    }
    window.location.href = '/';
  };

  if (checkingSession) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-container">
          <div className="reset-password-card">
            <div className="reset-password-logo">
              <div className="logo">
                <div className="logo-shield"><Shield size={50} /><span className="logo-text-k">K</span></div>
                <span>RIME</span>
              </div>
            </div>
            <div className="reset-password-content"><p>Loading...</p></div>
          </div>
        </div>
      </div>
    );
  }

  if (linkError) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-container">
          <div className="reset-password-card">
            <div className="reset-password-logo">
              <div className="logo">
                <div className="logo-shield"><Shield size={50} /><span className="logo-text-k">K</span></div>
                <span>RIME</span>
              </div>
            </div>
            <div className="reset-password-content">
              <h2>Link Expired</h2>
              <p className="reset-subtitle">{linkError}</p>
              <button className="reset-submit" onClick={() => window.location.href = '/'}>Back</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!hasSession && !success) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-container">
          <div className="reset-password-card">
            <div className="reset-password-logo">
              <div className="logo">
                <div className="logo-shield"><Shield size={50} /><span className="logo-text-k">K</span></div>
                <span>RIME</span>
              </div>
            </div>
            <div className="reset-password-content">
              <h2>Invalid Link</h2>
              <p className="reset-subtitle">This password reset link is invalid or has expired.</p>
              <button className="reset-submit" onClick={() => window.location.href = '/'}>Back</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="reset-password-logo">
            <div className="logo">
              <div className="logo-shield"><Shield size={50} /><span className="logo-text-k">K</span></div>
              <span>RIME</span>
            </div>
          </div>
          <div className="reset-password-content">
            {success ? (
              <div className="reset-success-state">
                <CheckCircle size={48} className="success-icon" />
                <h2>Password Updated!</h2>
                <p className="reset-subtitle">Your password has been successfully changed.</p>
                <button className="reset-submit" onClick={handleShowLogin}>Log In</button>
              </div>
            ) : (
              <>
                <h2>Reset Your Password</h2>
                <p className="reset-subtitle">Enter your new password below.</p>
                {error && <div className="reset-error">{error}</div>}
                <form onSubmit={handleResetPassword}>
                  <input 
                    type="password" 
                    placeholder="New password (min. 8 characters)" 
                    value={newPassword} 
                    onChange={(e) => setNewPassword(e.target.value)} 
                    required 
                    className="reset-input" 
                    minLength={8} 
                    autoComplete="new-password"
                  />
                  <input 
                    type="password" 
                    placeholder="Confirm new password" 
                    value={confirmPassword} 
                    onChange={(e) => setConfirmPassword(e.target.value)} 
                    required 
                    className="reset-input" 
                    autoComplete="new-password"
                  />
                  <button type="submit" className="reset-submit" disabled={loading}>{loading ? 'Updating...' : 'Reset Password'}</button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
      {showAuthModal && <AuthModal initialView="log_in" onClose={handleLoginSuccess} />}
    </div>
  );
};

export default ResetPassword;